import { useEffect, useRef, useState } from "react";
import {
  LuBug,
  LuChevronDown,
  LuClipboardList,
  LuCode,
  LuFileDown,
  LuImage,
  LuSettings,
  LuUpload,
} from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { components, typography } from "../../../design/tokens";
import {
  DashboardMode,
  useDashboardControlsActions,
  useWorkspaceSection,
  WorkspaceSection,
} from "../../../view_stores/useDashboardControlsStore";
import { useProjectName } from "../../../view_stores/useProjectConfigStore";
import {
  PreviewUrlStatus,
  useProjectPreviewStatus,
} from "../../../view_stores/useProjectPreviewStore";

export function ProjectWorkspaceMenu() {
  const isCompactView = useIsCompactView();

  const { setWorkspaceSection, setDashboardMode } =
    useDashboardControlsActions();

  const sections = [
    // {
    //   id: WorkspaceSection.OVERVIEW,
    //   label: "Overview",
    //   icon: <LuFileChartColumn size={20} />,
    // },
    {
      id: WorkspaceSection.SETTINGS,
      label: "App Settings",
      icon: <LuSettings size={20} />,
    },
    {
      id: WorkspaceSection.ICON,
      label: "App Icon",
      icon: <LuImage size={20} />,
    },
    {
      id: WorkspaceSection.CODE,
      label: "Code",
      icon: <LuCode size={20} />,
    },
    {
      id: WorkspaceSection.PUBLISH,
      label: "Publish",
      icon: <LuUpload size={20} />,
    },
    {
      id: WorkspaceSection.EXPORT,
      label: "Export",
      icon: <LuFileDown size={20} />,
    },
    ...(import.meta.env.VITE_PROD === "false"
      ? [
          {
            id: WorkspaceSection.DEBUG,
            label: "Debug",
            icon: <LuBug size={20} />,
          },
        ]
      : []),
  ];

  function handleSectionClick(section: WorkspaceSection) {
    setDashboardMode(DashboardMode.WORKSPACE);
    setWorkspaceSection(section);
  }

  return isCompactView ? (
    <CompactMenu sections={sections} handleSectionClick={handleSectionClick} />
  ) : (
    <SideMenu sections={sections} handleSectionClick={handleSectionClick} />
  );
}

function CompactMenu({
  sections,
  handleSectionClick,
}: {
  sections: {
    id: WorkspaceSection;
    label: string;
    icon: React.ReactNode;
  }[];
  handleSectionClick: (section: WorkspaceSection) => void;
}) {
  const projectName = useProjectName();
  const previewStatus = useProjectPreviewStatus();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSectionClick = (section: WorkspaceSection) => {
    setIsDropdownOpen(false);
    handleSectionClick(section);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`${
          isDropdownOpen
            ? "text-dark-text bg-dark-surface-secondary"
            : "text-dark-text bg-dark-surface-primary"
        } flex items-center gap-1.5 px-3 py-1.5 rounded-md
        ${typography.size.sm} ${typography.weight.medium} transition-colors`}
        disabled={
          !projectName || previewStatus === PreviewUrlStatus.NotAvailable
        }
      >
        <LuClipboardList size={18} />
        Workspace
        <LuChevronDown
          size={18}
          className={`transition-transform ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isDropdownOpen && (
        <div
          className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 ${components.dropdown.container} z-50`}
        >
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => onSectionClick(section.id)}
              className={`${components.dropdown.item} py-2.5 gap-3`}
            >
              {section.icon}
              <span>{section.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SideMenu({
  sections,
  handleSectionClick,
}: {
  sections: {
    id: WorkspaceSection;
    label: string;
    icon: React.ReactNode;
  }[];
  handleSectionClick: (section: WorkspaceSection) => void;
}) {
  const [canShowLabels, setCanShowLabels] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const workspaceSection = useWorkspaceSection();

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanShowLabels(entry.contentRect.width > 100);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="h-full w-full px-4 py-4">
      <nav className="space-y-1">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className={`${canShowLabels ? "justify-start" : "justify-center"} ${
              workspaceSection === section.id
                ? "text-dark-text bg-dark-surface-secondary"
                : "text-dark-text hover:bg-dark-surface-secondary"
            } w-full min-w-[40px] flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
              typography.size.sm
            }`}
            title={section.label}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              {section.icon}
            </div>
            {canShowLabels && (
              <span className="side-menu-label font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                {section.label}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}
